import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import { Loader2 } from 'lucide-react';

const phoneRegex = /^\+?\d{10}$/;

// Zod Schema
const formSchema = z.object({
  email: z.string().email({ message: 'Enter a valid work email' }),
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  company: z.string().min(1, { message: 'Company name is required' }),
  phoneNumber: z.string().regex(phoneRegex, {
    message: 'Enter a valid phone number',
  }),
  role: z.string().min(1, { message: 'Role is required' }),
});

const BookDemoFormScreen = ({ setScreen, setData }) => {
  const [processing, setProcessing] = useState(false);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      phoneNumber: '',
      role: '',
    },
  });

  const handleSubmit = (formData) => {
    setProcessing(true);
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      metadata: {
        company_name: formData.company,
        phone_number: formData.phoneNumber,
        role: formData.role,
      },
    };

    axios
      .post('https://backend.makersharks.ai/user-waitlist', payload, {
        headers: {
          'X-Api-Key': 'bc631e4efc8a7e9bd6ee37d9ad0ff3c8',
          'Content-Type': 'application/json',
        },
      })
      .then((response) => {
        setProcessing(false);

        console.log('Form Data:', payload);
        setData(payload);
        setScreen('CALENDER');

        form.reset({
          first_name: '',
          last_name: '',
          company_name: '',
          email: '',
          phone_number: '',
          role: '',
        });
        setScreen('CALENDER');
      })
      .catch((error) => {
        console.log('Error: ', error);
        setProcessing(false);
      });
  };

  const sendEmail = (formData) => {
    emailjs
      .send(
        'Makersharks_waitlist',
        'template_y94jvth',
        {
          to_name: formData.first_name,
          to_email: formData.email,
        },
        {
          publicKey: '_2Z_97_GgLa7_oicl',
        },
      )
      .then(
        () => {
          console.log('SUCCESS!');
        },
        (error) => {
          console.log('FAILED...', error.text);
        },
      );
  };

  return (
    <>
      <div className="flex items-center mb-6 gap-3">
        <img src="/blackLogo.svg" alt="Logo" />
        <h2 className="font-bold text-4xl">MakerSharks</h2>
      </div>
      <div className="py-4 px-10 text-base">
        <h2 className="text-2xl font-semibold mt-4">
          Book a demo to explore MakerSharks
        </h2>
        <p className="text-gray-600 mt-1 leading-tight mb-8">
          Makersharks has helped hundreds of companies save months. We'd love to
          show you how in 15 minutes!
        </p>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6"
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                e.target.tagName === 'INPUT' &&
                e.target.tagName !== 'TEXTAREA' &&
                !e.target.getAttribute('data-allow-enter')
              ) {
                e.preventDefault();
              }
            }}
          >
            {/* First Name */}
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-base">First Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John"
                      className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple bg-gray-50 border-gray-300"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Last Name */}
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-base">Last Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Doe"
                      className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple bg-gray-50 border-gray-300"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="md:col-span-2 space-y-1">
                  <FormLabel className="text-base">Work Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="email@company.com"
                      className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple bg-gray-50 border-gray-300"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Phone Number */}
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-base">Phone Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="+91XXXXXXXXXX"
                      className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple bg-gray-50 border-gray-300"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Role */}
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-base">Your Role</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Product Manager"
                      className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple bg-gray-50 border-gray-300"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Company */}
            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem className="md:col-span-2 space-y-1">
                  <FormLabel className="text-base">Company</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Your Company Name"
                      className="focus:ring-0 focus:ring-offset-0 text-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple bg-gray-50 border-gray-300"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="md:col-span-2 mt-4">
              <Button
                type="submit"
                disabled={!form.formState.isValid || processing}
                className={`w-full focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple ease-in duration-300 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${processing ? 'cursor-not-allowed bg-gray-400' : 'bg-darkPurple text-white hover:bg-darkerPurple'}`}
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Next'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
};

export default BookDemoFormScreen;
